/** Zod-free, so Convex imports it too: the private demo caps, refusals and the held-send header. */
/** Ask Lionshare questions one person may ask a day across their private demo copies. Raise here; nothing else changes. */
export const DEMO_QUESTIONS_PER_DAY = 200;
/** New files one person's private demo copies may read with a model (documents, images, voice), ever. Reset does not refill it. */
export const DEMO_FILE_READS_TOTAL = 25;
/** The day a question counts against, in the demo's own zone. */
export const DEMO_CAP_TIME_ZONE = 'America/New_York';

/** What a private copy refuses, and the one plain sentence the person sees. */
export const DEMO_COPY_REFUSALS = {
  demo_copy_no_send: { status: 200, message: 'This is your private demo, so nothing was sent.' },
  demo_team_removed: { status: 409, message: 'This team demo was removed. Send a new invite from Demo copies to build a new team demo.' },
  demo_copy_preparing: { status: 409, message: 'Your demo is still being prepared. Try again in a minute.' },
  demo_copy_no_invites: { status: 403, message: "The demo template's managers invite people from Demo copies." },
  demo_copy_no_mail: { status: 403, message: 'This is your private demo, so letters are not mailed.' },
  demo_question_cap: { status: 429, message: `You’ve asked ${DEMO_QUESTIONS_PER_DAY} questions today, the most for a demo. Ask again tomorrow.` },
  demo_file_read_cap: { status: 429, message: `Your demo has read ${DEMO_FILE_READS_TOTAL} files, the most it can. The file is kept but not read.` },
  demo_copy_no_model: { status: 403, message: 'Lionshare can’t use AI for this in your private demo.' },
  demo_copy_forbidden: { status: 403, message: 'Only the managers of the demo template can see demo copies.' },
  demo_template_locked: { status: 409, message: 'This is the demo template. Open your own copy to make changes, or turn on Edit the template in Demo copies.' },
} as const;
export type DemoCopyRefusalCode = keyof typeof DEMO_COPY_REFUSALS;

/** The response header a private copy's reply carries when something the request would have sent was held back. */
export const DEMO_COPY_HELD_HEADER = 'x-lionshare-demo-held';

/**
 * The request header a template manager's write into a demo template carries, naming the template, only while they have
 * turned on Edit the template in Demo copies. Without it the API refuses every write there (`demo_template_locked`).
 */
export const DEMO_TEMPLATE_EDIT_HEADER = 'x-lionshare-edit-template';

/** The calendar day a question counts against. */
export function demoCapDay(nowMs: number): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: DEMO_CAP_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(nowMs);
}

export type DemoPolicy = { questionsPerDay?: number | null; fileReadsTotal?: number; teamCopy?: boolean };

export function resolveDemoPolicy(policy?: DemoPolicy) {
  return { questionsPerDay: policy?.questionsPerDay === null ? null : policy?.questionsPerDay ?? DEMO_QUESTIONS_PER_DAY,
    fileReadsTotal: policy?.fileReadsTotal ?? DEMO_FILE_READS_TOTAL, teamCopy: policy?.teamCopy ?? false };
}

/** Refusal text uses the same policy as the gate and the settings pages. */
export function demoCopyRefusal(code: DemoCopyRefusalCode, policy?: DemoPolicy): { status: number; message: string } {
  const caps = resolveDemoPolicy(policy);
  if (code === 'demo_question_cap') return { status: 429, message: `You’ve asked ${caps.questionsPerDay} ${caps.questionsPerDay === 1 ? 'question' : 'questions'} today, the most for a demo. Ask again tomorrow.` };
  if (code === 'demo_file_read_cap') return { status: 429, message: `Your demo has read ${caps.fileReadsTotal} ${caps.fileReadsTotal === 1 ? 'file' : 'files'}, the most it can. The file is kept but not read.` };
  return DEMO_COPY_REFUSALS[code];
}
